/**
 * Le compte bancaire d'un virement, et la famille qu'il désigne.
 *
 * ## La demande
 *
 * Signalé le 20 septembre 2026 : « Pas moyen de voir le compte émetteur, il
 * serait bien utile. » L'écran affichait « bénéficiaire inconnu » et s'arrêtait
 * là — sans dire si le compte manquait, ou s'il était là sans qu'on sache à qui
 * il appartient. Ce sont deux situations différentes, et elles n'appellent pas
 * le même geste.
 *
 * ## Ce que la base savait déjà
 *
 * 17 646 encaissements sur 17 717 portent l'IBAN du payeur. Chaque fois qu'un
 * de ces encaissements a été rapproché, il a lié un compte à une famille —
 * **1 436 comptes, 1 315 familles** — sans que rien ne le lise.
 *
 * ## Les quatre états, et pourquoi ils ne se confondent pas
 *
 * - `reconnu` — un seul foyer a payé depuis ce compte. On le nomme, avec sa
 *   preuve : le nombre de versements reçus.
 * - `partage` — plusieurs foyers. **On ne choisit pas.** 27 comptes sont dans
 *   ce cas (parents séparés, un grand-parent qui paie pour deux foyers), et
 *   désigner l'un des deux rattacherait un remboursement au mauvais.
 * - `inconnu` — le compte est écrit sur le mouvement, mais aucun encaissement
 *   rapproché n'en vient. C'est peut-être un fournisseur ; c'est peut-être une
 *   famille dont le premier versement n'est pas encore placé.
 * - `absent` — le relevé n'a pas donné de compte du tout. Rien à chercher :
 *   c'est la LECTURE du relevé qu'il faudrait reprendre, pas le rapprochement.
 *
 * Le dernier état est le plus important à distinguer. Sans lui, un mouvement
 * dont le relevé n'a rien dit se lit comme un mouvement dont on n'a pas trouvé
 * le propriétaire — et on cherche pour rien.
 */

/** Un compte relié à une famille, tel que `v_comptes_des_familles` le rend. */
export interface CompteDeFamille {
  iban: string;
  userId: string;
  famille: string;
  versements: number;
  dernierVersement: string | null;
  /** Le nom tel que la BANQUE l'écrit : « MME SARAH LACOUR ». */
  nomAuReleve: string | null;
  /** Vrai quand ce compte a payé pour plusieurs foyers. */
  partage: boolean;
}

export type EtatDuCompte = 'reconnu' | 'partage' | 'inconnu' | 'absent';

export interface LectureDuCompte {
  etat: EtatDuCompte;
  /** L'IBAN normalisé, quand le mouvement en porte un. */
  iban: string | null;
  /** L'IBAN tel qu'on l'affiche, par groupes de quatre. */
  ibanLisible: string | null;
  /** La famille, seulement quand un seul foyer paie depuis ce compte. */
  compte: CompteDeFamille | null;
  /** Ce que l'écran affiche, sans rien y ajouter. */
  phrase: string;
}

/** La forme normalisée d'un IBAN : la même que celle de la vue SQL. */
export function normaliserIban(iban: string | null | undefined): string | null {
  const n = (iban ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return n === '' ? null : n;
}

/** `BE25 3770 4697 1982` — la forme que l'on relit sur un relevé. */
export function ibanLisible(iban: string | null | undefined): string | null {
  const n = normaliserIban(iban);
  return n ? (n.match(/.{1,4}/g) ?? [n]).join(' ') : null;
}

export function lireLeCompte(
  mouvement: { counterpartyIban?: string | null; counterpartyName?: string | null },
  comptes: CompteDeFamille[],
): LectureDuCompte {
  const iban = normaliserIban(mouvement.counterpartyIban);
  const lisible = ibanLisible(iban);

  if (!iban) {
    return {
      etat: 'absent',
      iban: null,
      ibanLisible: null,
      compte: null,
      phrase: mouvement.counterpartyName
        ? `Le relevé ne donne que le nom : ${mouvement.counterpartyName}. Aucun numéro de compte.`
        : "Le relevé de ce mouvement n'a donné ni nom ni numéro de compte.",
    };
  }

  const pourCeCompte = comptes.filter((c) => normaliserIban(c.iban) === iban);

  if (pourCeCompte.length === 0) {
    return {
      etat: 'inconnu',
      iban,
      ibanLisible: lisible,
      compte: null,
      phrase: `Aucun encaissement rapproché ne vient de ${lisible}`
        + `${mouvement.counterpartyName ? ` (${mouvement.counterpartyName})` : ''}.`,
    };
  }

  if (pourCeCompte.length > 1 || pourCeCompte[0].partage) {
    const noms = pourCeCompte.map((c) => c.famille).join(', ');
    return {
      etat: 'partage',
      iban,
      ibanLisible: lisible,
      compte: null,
      phrase: `${lisible} a payé pour plusieurs foyers — ${noms}. `
        + `Il ne désigne donc personne à lui seul.`,
    };
  }

  const c = pourCeCompte[0];
  return {
    etat: 'reconnu',
    iban,
    ibanLisible: lisible,
    compte: c,
    phrase: `${lisible} est le compte de ${c.famille} : `
      + `${c.versements} versement${c.versements > 1 ? 's' : ''} reçu${c.versements > 1 ? 's' : ''} de ce compte`
      + `${c.nomAuReleve ? `, au nom de « ${c.nomAuReleve} »` : ''}.`,
  };
}
